import React, { Component } from 'react';
import { Row, Progress, FormGroup, Input, Label, Alert } from 'reactstrap';
import ClientCalender from '../../../elements/Booking/Customer/ClientCalender';
import SingleDateSelection from '../../../elements/Booking/Customer/SingleDateSelection';
import Tracking from '../../../elements/Booking/Customer/Tracking';
import LeadForm from '../../../elements/Booking/Customer/LeadForm';
import ConfirmData from '../../../elements/Booking/Customer/ConfirmData';
import axios from 'axios';
import { getCurrencySymbol } from '../../../helpers/currency';
import queryString from 'query-string';
import BookingSiteContainer from './BookingSiteContainer';
import Texts from '../../../json/texts.json';
import { getTranslationKey } from '../../../modules/utils/helpers';
import { RouteComponentProps } from 'react-router-dom';

interface Product {
  _id: string;
  title: string;
  description: string;
  price: number;
  currency: string;
  duration: number;
}

interface Lead {
  first_name: string;
  last_name: string;
  email: string;
  phone_no?: string;
  zipcode?: string;
}

interface User {
  _id: string;
  company: string;
  first_name: string;
  last_name: string;
  street: string;
  zipcode: string;
  city: string;
  email: string;
}

// Local appointment type that can work with both ClientCalender and SingleDateSelection
interface BookingAppointment {
  id?: number;
  start: Date;
  end: Date;
  index?: number;
}

interface AvailableDay {
  weekday: number;
  active: boolean;
  start: number;
  end: number;
}

interface BookingSite {
  _id: string;
  title: string;
  image: string;
  description?: string;
  type: 'shooting' | 'consult' | 'gift';
  active?: boolean;
  products: Product[];
  phone_no_required?: boolean;
  availableTimesType?: string;
  _user: User;
  buffer?: number;
  availableTimes?: AvailableDay[];
  availableAppointmentsCount?: number;
  availableDaysInAdvance?: number;
  bufferBeforeFirstAppointment?: number;
  duration?: number;
  thankYouUrl?: string;
  purchaseOnInvoice?: boolean;
}

interface BookingSiteState {
  product: Product | Record<string, never>;
  lead: Lead | Record<string, never>;
  bookingSite: BookingSite | Record<string, never>;
  step?: number;
  maxSteps?: number;
  appointment?: BookingAppointment;
  clientCalenderPage?: number;
  status?: 'success' | 'danger' | 'blocked' | null;
  loading?: boolean;
  purchaseOnInvoiceState?: boolean;
}

class BookingSite extends Component<RouteComponentProps<{ id: string }>, BookingSiteState> {
  constructor(props: RouteComponentProps<{ id: string }>) {
    super(props);
    this.state = {
      product: {},
      lead: {},
      bookingSite: {}
    };

    this.nextStep = this.nextStep.bind(this);
    this.prevStep = this.prevStep.bind(this);
    this.saveLead = this.saveLead.bind(this);
    this.onVoucherChange = this.onVoucherChange.bind(this);
    this.toggleProduct = this.toggleProduct.bind(this);
    this.submitBooking = this.submitBooking.bind(this);
    this.getAppointmentLength = this.getAppointmentLength.bind(this);
    this.clientCalenderPageChanged = this.clientCalenderPageChanged.bind(this);
    this.onSelectAppointment = this.onSelectAppointment.bind(this);
    this.handlePurchaseOnInvoice = this.handlePurchaseOnInvoice.bind(this);
    this.purchaseByPayPal = this.purchaseByPayPal.bind(this);
  }

  UNSAFE_componentWillMount(): void {
    this.loadBookingSite(this.props.match.params.id);

    const parsed = queryString.parse(this.props.location.search);
    const { token } = parsed;
    if (token) {
      alert(getTranslationKey('bookingAborted'));
    }
  }

  handlePurchaseOnInvoice(): void {
    this.setState({ purchaseOnInvoiceState: true }, this.submitBooking);
  }

  onVoucherChange(e: React.ChangeEvent<HTMLInputElement>): void {
    if (e.target.value === '-1') {
      this.setState({
        product: {}
      });
    } else {
      const product = this.getProductFromBookingSiteById(e.target.value);
      this.setState({
        product
      });
    }
  }

  onSelectAppointment(appointment: BookingAppointment): void {
    this.setState({ appointment });
  }

  getProductFromBookingSiteById(id: string): Product {
    return (this.state.bookingSite as BookingSite).products.filter(
      (product) => product._id === id
    )[0];
  }

  getAppointmentLength(): number {
    const { bookingSite } = this.state;

    return (bookingSite as BookingSite).type === 'consult'
      ? (bookingSite as BookingSite).duration!
      : (this.state.product as Product).duration;
  }

  async submitBooking(): Promise<void> {
    try {
      this.setState({ loading: true });
      const { type, thankYouUrl } = this.state.bookingSite as BookingSite;
      const result = await axios.post(`/api/booking/create/${type}`, this.state);
      const { success, redirectUrl, blockedAppointment } = result.data;
      if (success) {
        if (type === 'consult' || this.state.purchaseOnInvoiceState) {
          if (thankYouUrl) {
            window.location.href = thankYouUrl;
          } else {
            this.setState({ status: 'success', loading: false });
          }
        } else {
          if (redirectUrl) {
            this.setState({ status: null });
            window.location.href = redirectUrl;
          } else {
            this.setState({ status: 'danger', loading: false });
          }
        }
      } else {
        if (blockedAppointment) {
          this.setState({ status: 'blocked', loading: false });
        } else {
          this.setState({ status: 'danger', loading: false });
        }
      }
    } catch (e) {
      console.log(e);
      this.setState({ status: 'danger', loading: false });
    }
  }

  purchaseByPayPal(): void {
    this.setState({ purchaseOnInvoiceState: false }, this.submitBooking);
  }

  clientCalenderPageChanged(clientCalenderPage: number): void {
    this.setState({ clientCalenderPage });
  }

  toggleProduct(product: Product): void {
    this.setState({
      product: {
        ...product
      }
    });
  }

  saveLead(values: Lead): void {
    this.setState({
      lead: {
        ...values
      }
    });
    this.nextStep();
  }

  nextStep(): void {
    this.setState((prevState) => ({ step: prevState.step! + 1 }));
  }

  prevStep(): void {
    this.setState((prevState) => ({
      step: prevState.step! - 1,
      status: null as 'success' | 'danger' | 'blocked' | null
    }));
  }

  async loadBookingSite(bookingSiteId: string): Promise<void> {
    if (bookingSiteId) {
      try {
        const result = await axios.get(`/api/bookingsite?id=${bookingSiteId}`);
        const bookingSite = result.data;
        const step = 0;
        const maxSteps = bookingSite.type === 'shooting' ? 3 : 2;
        this.setState({ bookingSite, step, maxSteps });
      } catch (e) {
        console.log(e);
      }
    }
  }

  renderCompanyInfo(): JSX.Element | undefined {
    if (this.state.step === 0) {
      const { company, first_name, last_name, street, zipcode, city, email } = (
        this.state.bookingSite as BookingSite
      )._user;

      return (
        <div>
          <Row>
            <div className="col-12 col-md-6">
              <h2>{company}</h2>
              <p>
                {first_name + ' ' + last_name}
                <br />
                {street}
                <br />
                {zipcode + ' ' + city}
                <br />
                <a href={`mailto:${email}`} target="_blank" rel="noopener noreferrer">
                  {email}
                </a>
              </p>
            </div>
            <div className="col-12 col-md-6">
              <p>{(this.state.bookingSite as BookingSite).description}</p>
            </div>
          </Row>
          <hr />
        </div>
      );
    }
  }

  renderProgress(): JSX.Element {
    const { step, maxSteps, bookingSite } = this.state;

    return (
      <Row>
        <div className="col-12">
          <div className="text-center m-b-20">
            <h4>
              {getTranslationKey('step')} {step! + 1} -{' '}
              {getTranslationKey(`bookingSteps.${(bookingSite as BookingSite).type}.${step}`)}
            </h4>
          </div>
          <Progress
            className="m-b-20 h-30"
            animated
            color="success"
            value={step === 0 ? 0.3 : step!}
            max={maxSteps}
          />
        </div>
      </Row>
    );
  }

  renderConfirmOverview(): JSX.Element {
    const { status, bookingSite } = this.state;

    let bookButtonText = '';
    let bookButtonFunction: (() => void) | null = null;

    if ((bookingSite as BookingSite).type === 'consult') {
      bookButtonText = getTranslationKey('bookButton');
      bookButtonFunction = this.submitBooking;
    } else {
      bookButtonText = getTranslationKey('goToPayPal');
      bookButtonFunction = this.purchaseByPayPal;
    }

    const buttonTexts =
      bookButtonText +
      ((bookingSite as BookingSite).purchaseOnInvoice
        ? '/' + getTranslationKey('purchaseOnInvoice')
        : '');

    return (
      <div>
        <ConfirmData
          lead={this.state.lead as Lead}
          product={this.state.product as Product}
          appointment={this.state.appointment}
        />
        <button
          className="btn btn-primary w-100-p btn-booking m-b-5"
          onClick={bookButtonFunction!}
          disabled={status === 'success'}
        >
          {bookButtonText}
        </button>
        {(bookingSite as BookingSite).purchaseOnInvoice && (
          <button
            className="btn btn-primary w-100-p btn-booking m-b-5"
            onClick={this.handlePurchaseOnInvoice}
            disabled={status === 'success'}
          >
            {getTranslationKey('purchaseOnInvoice')}
          </button>
        )}
        <div className="dataprivacy-hint-wrapper">
          <small>{getTranslationKey('dataprivacyHint', { button: buttonTexts })}</small>
        </div>
        {status && (
          <Alert className="m-t-10" color={status === 'blocked' ? 'danger' : status}>
            {Texts[0][`booking-${status}`]}
          </Alert>
        )}
      </div>
    );
  }

  renderLeadForm(): JSX.Element {
    const { phone_no_required } = this.state.bookingSite as BookingSite;

    return <LeadForm saveLead={this.saveLead} phone_no_required={phone_no_required} />;
  }

  renderShooting(): JSX.Element {
    switch (this.state.step) {
      case 2:
        return this.renderLeadForm();
      case 0:
        const { products } = this.state.bookingSite as BookingSite;

        return (
          <div className="row">
            <div className="col">
              {products.map((product, key) => (
                <FormGroup key={key} check>
                  <Label className="w-100-p" check>
                    <Input
                      type="radio"
                      checked={(this.state.product as Product)._id === product._id}
                      onClick={() => this.toggleProduct(product)}
                      className="form-group-booking-product"
                    />
                    <h3 className="inline">{product.title}</h3>
                    <h3 className="float-right inline">
                      {product.price + getCurrencySymbol(product.currency)}
                    </h3>
                    <p>{product.description}</p>
                  </Label>
                </FormGroup>
              ))}

              <button
                className="btn btn-success w-100-p btn-booking"
                disabled={!(this.state.product as Product)._id}
                onClick={this.nextStep}
                type="submit"
              >
                {getTranslationKey('nextStep')}
              </button>
            </div>
          </div>
        );
      case 1:
        return this.renderAppointmentSelection();
      case 3:
        return this.renderConfirmOverview();
      default:
        return <p>Error</p>;
    }
  }

  renderConsult(): JSX.Element {
    switch (this.state.step) {
      case 1:
        return this.renderLeadForm();
      case 0:
        return this.renderAppointmentSelection();
      case 2:
        return this.renderConfirmOverview();
      default:
        return <p>Error</p>;
    }
  }

  renderAppointmentSelection(): JSX.Element {
    const { appointment, clientCalenderPage, bookingSite } = this.state;
    const {
      availableTimesType,
      buffer,
      _id,
      availableTimes,
      availableAppointmentsCount,
      availableDaysInAdvance,
      bufferBeforeFirstAppointment
    } = bookingSite as BookingSite;

    if (availableTimesType === 'availableTimes') {
      return (
        <ClientCalender
          // Type assertion needed: BookingAppointment is compatible but not identical to ClientCalender's Appointment type
          selectedAppointment={appointment as unknown as BookingAppointment & { id: number }}
          onSelectAppointment={
            this.onSelectAppointment as unknown as (
              appointment: BookingAppointment & { id: number }
            ) => void
          }
          onSelectionEnd={this.nextStep}
          buffer={buffer}
          bookingSiteId={_id}
          available={availableTimes}
          availableDaysInAdvance={availableDaysInAdvance!}
          bufferBeforeFirstAppointment={bufferBeforeFirstAppointment}
          appointmentLengthMinutes={this.getAppointmentLength()}
          availableAppointmentsCount={availableAppointmentsCount}
          page={clientCalenderPage || 1}
          onSelectedPageChange={this.clientCalenderPageChanged}
        />
      );
    }

    return (
      <SingleDateSelection
        bookingSiteId={_id}
        // Type assertion needed: BookingAppointment is compatible but not identical to SingleDateSelection's SelectedAppointment type
        selectedAppointment={appointment as unknown as BookingAppointment & { index: number }}
        onSelectAppointment={
          this.onSelectAppointment as unknown as (
            appointment: BookingAppointment & { index: number }
          ) => void
        }
        onSelectionEnd={this.nextStep}
      />
    );
  }

  renderGift(): JSX.Element {
    switch (this.state.step) {
      case 1:
        return this.renderLeadForm();
      case 0:
        const { products } = this.state.bookingSite as BookingSite;

        return (
          <div>
            <FormGroup>
              <Label>{getTranslationKey('selectVoucherValue')}</Label>
              <Input
                value={(this.state.product as Product)._id}
                onChange={this.onVoucherChange}
                type="select"
              >
                <option value="-1">{getTranslationKey('pleaseSelect')}</option>
                {products.map((product, key) => (
                  <option value={product._id} key={key}>
                    {product.price + getCurrencySymbol(product.currency)}
                  </option>
                ))}
              </Input>
            </FormGroup>
            <button
              className="btn btn-success w-100-p btn-booking"
              disabled={!(this.state.product as Product)._id}
              onClick={this.nextStep}
              type="submit"
            >
              {getTranslationKey('nextStep')}
            </button>
          </div>
        );
      case 2:
        return this.renderConfirmOverview();
      default:
        return <p>Error</p>;
    }
  }

  renderByType(): JSX.Element {
    switch ((this.state.bookingSite as BookingSite).type) {
      case 'shooting':
        return this.renderShooting();
      case 'consult':
        return this.renderConsult();
      case 'gift':
        return this.renderGift();
      default:
        return this.renderShooting();
    }
  }

  render(): JSX.Element {
    const { bookingSite, loading } = this.state;
    if (!(bookingSite as BookingSite)._id || loading) {
      return (
        <div className="loading">
          <div className="loader" />
        </div>
      );
    }

    if ((bookingSite as BookingSite).active === false) {
      return (
        <h1 className="text-center p-20">
          {getTranslationKey('bookingSiteDisabled')}
          <br />
          {getTranslationKey('pleaseTryOtherTime')}
        </h1>
      );
    }

    return (
      <BookingSiteContainer bookingSite={bookingSite as BookingSite}>
        {this.renderCompanyInfo()}
        {this.renderProgress()}
        {this.renderByType()}
        {this.state.step !== 0 && (
          <div className="text-center m-t-10">
            <span onClick={this.prevStep} className="back-button">
              {getTranslationKey('back')}
            </span>
          </div>
        )}
        <Tracking bookingSite={bookingSite as BookingSite} />
      </BookingSiteContainer>
    );
  }
}

export default BookingSite;
